<script setup lang="ts">
import type { UseProjectData } from '~/store-v2/projects/useProject'
import type { Project } from '~/utils/project/Project'
import { SolButton, SolSelect } from '@solfacil/girassol'
import IconInfo from '~icons/material-symbols/info-outline'
import IconReceipt from '~icons/material-symbols/receipt-long-outline'
import IconCart from '~icons/material-symbols/shopping-cart-outline'
import ComboConecteModal from '~/components/simulation/ComboConecteModal.vue'
import useHardwareForm from '~/store-v2/hardware/useHardwareForm'
import useAvailableOrders from '~/store-v2/payment-model/useAvailableOrders'

const props = defineProps<{
  projectId: string
  project: UseProjectData
  orderId?: string | null
  disabled?: boolean
  storeDisabled?: boolean
  error?: string
}>()

const emit = defineEmits<{
  (e: 'update:orderId', value: string): void
  (e: 'goToStore'): void
}>()

const { t } = useI18n()

const comboProject = computed(() => (props.project ?? null) as unknown as Project | null)

const projectIdRef = computed(() => props.projectId)
const { data: availableOrders, isLoading } = useAvailableOrders(projectIdRef)
const { data: hardwareForm } = useHardwareForm(projectIdRef)

function buildOptionLabel(id: string | number, value: number) {
  return t('customer.biometry.compatibility.order_option', {
    id,
    value: value.toLocaleString('pt-BR', { minimumFractionDigits: 2 }),
  })
}

const orderOptions = computed(() => {
  const options = (availableOrders.value?.data ?? []).map(order => ({
    name: buildOptionLabel(order.order_id, Number(order.value)),
    value: String(order.order_id),
  }))

  const linkedOrderId = props.orderId ? String(props.orderId) : ''
  const isLinkedOrderMissing = linkedOrderId
    && !options.some(option => option.value === linkedOrderId)

  if (isLinkedOrderMissing) {
    const linkedValue = hardwareForm.value?.order_store?.equipment_value ?? 0
    options.unshift({
      name: buildOptionLabel(linkedOrderId, linkedValue),
      value: linkedOrderId,
    })
  }

  return options
})

const selectedOrderId = ref<string>(props.orderId ? String(props.orderId) : '')
watch(() => props.orderId, (value) => {
  if (value)
    selectedOrderId.value = String(value)
})

const selectedOrder = computed(() =>
  orderOptions.value.find(option => option.value === selectedOrderId.value),
)

function handleSelectOrder(option?: { name: string, value: string }) {
  if (props.disabled || isLoading.value)
    return

  selectedOrderId.value = option?.value ?? ''
  emit('update:orderId', selectedOrderId.value)
}

const showInvertersModal = ref(false)

function handleGoToStore() {
  if (props.storeDisabled)
    return

  emit('goToStore')
}
</script>

<template>
  <div class="flex flex-col gap-4 p-6 rounded-md bg-neutral-high-light">
    <div class="flex flex-col gap-1">
      <span class="font-highlight text-neutral-low-dark text-[16px]">{{ t('customer.biometry.compatibility.title') }}</span>
      <span class="fonts-body-medium-regular text-brand-primary-medium">
        {{ t('customer.biometry.compatibility.consult_before') }}
        <button type="button" class="text-brand-secondary-dark font-bold underline" @click="showInvertersModal = true">{{ t('customer.biometry.compatibility.consult_link') }}</button>.
      </span>
      <span class="fonts-body-medium-regular text-brand-primary-medium">
        {{ t('customer.biometry.compatibility.consult_after') }}
      </span>
    </div>

    <div class="flex flex-col md:system:flex-row md:system:items-end gap-6">
      <SolSelect
        id="combo_connect_order"
        name="combo_connect_order"
        class="w-full"
        :label="`${t('customer.biometry.compatibility.order_label')}*`"
        :searchable="true"
        :options="orderOptions"
        :selected="selectedOrder"
        :disabled="disabled || isLoading"
        :placeholder="t('customer.biometry.compatibility.order_placeholder')"
        :error="error"
        @update:selected="handleSelectOrder($event as any)"
      />
      <SolButton
        id="go_to_store"
        variant="secondary"
        size="medium"
        class="w-full md:system:w-auto whitespace-nowrap"
        :disabled="storeDisabled"
        @click="handleGoToStore"
      >
        <template #icon:left>
          <IconCart />
        </template>
        {{ t('customer.biometry.compatibility.go_to_store') }}
      </SolButton>
    </div>

    <div class="flex flex-col md:system:flex-row md:system:items-center md:system:justify-between gap-2 mt-auto">
      <SolButton
        id="see_compatible_inverters"
        variant="secondary"
        size="small"
        class="w-full md:system:w-auto whitespace-nowrap"
        @click="showInvertersModal = true"
      >
        <template #icon:left>
          <IconReceipt />
        </template>
        {{ t('customer.biometry.compatibility.see_inverters') }}
      </SolButton>

      <span class="flex items-center gap-1 fonts-body-small-regular text-neutral-low-light">
        <IconInfo class="shrink-0 w-5 h-5 text-neutral-low-light" />
        {{ t('customer.biometry.compatibility.change_later') }}
      </span>
    </div>

    <ComboConecteModal
      :is-open="showInvertersModal"
      :project="comboProject"
      initial-tab="inverters"
      @update:is-open="showInvertersModal = $event"
    />
  </div>
</template>
